import { BASE_URL } from "@/Store/Action";
import { Button } from "flowbite-react";
import { Check, ChevronLeft, ChevronRight, Copy, X } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import ModalTracking from "../../Components/MyPage/TrackingImageGuideline ";
import Swal from "sweetalert2";

interface GuidelineItem {
  title: string;
  description: string;
}

function Review({
  onPrev,
  onNext,
  guideline,
}: {
  onPrev: () => void;
  onNext: () => void;
  guideline: any;
}) {
  const { secretkey } = useParams<{ secretkey: string }>();
  const [blogUrl, setBlogUrl] = useState("");
  const [showGuideline, setShowGuideline] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [copiedKeyword, setCopiedKeyword] = useState<string | null>(null);

  const keywords = guideline.keyword ? guideline.keyword.split(", ") : [];
  const guidelines = guideline.posting_keyword_guidelines
    ? JSON.parse(guideline.posting_keyword_guidelines)
    : [];

  const submitReview = async () => {
    if (!blogUrl.trim()) {
      Swal.fire({
        icon: "warning",
        title: "블로그 리뷰 주소를 입력해 주세요",
        html: `
    <div class="text-sm text-gray-600">
      블로그 리뷰 URL을 입력해야 합니다.<br/>
      예: <span class="text-blue-600 underline">https://blog.naver.com/블로그아이디/123456789</span>
    </div>
  `,
        showConfirmButton: true,
        confirmButtonText: "확인했어요",
        customClass: {
          confirmButton:
            "bg-yellow-500 text-white px-6 py-2 rounded-full text-sm",
          popup: "rounded-lg shadow text-center",
        },
      });
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/wg/review`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          secret_key: secretkey,
          blog_url: blogUrl,
        }),
      });

      if (response.status === 409) {
        Swal.fire({
          icon: "info",
          title: "이미 리뷰가 등록되었습니다",
          html: `
      <div class="text-sm text-gray-600">
        이미 리뷰를 제출하셨어요.<br/>
        관리자 확인을 기다려주세요
      </div>
    `,
          showConfirmButton: true,
          confirmButtonText: "확인",
          customClass: {
            confirmButton:
              "bg-blue-500 text-white px-6 py-2 rounded-full text-sm",
            popup: "rounded-lg shadow text-center",
          },
        });
        return;
      }

      if (!response.ok) {
        showErrorWithGuideButton(setShowGuideline);
        return;
      }

      setShowSuccessModal(true);
    } catch (err) {
      showErrorWithGuideButton(setShowGuideline);
    }
  };

  const showErrorWithGuideButton = (
    setShowGuideline: (value: boolean) => void
  ) => {
    Swal.fire({
      icon: "error",
      title: "리뷰 링크 확인 필요",
      html: `
        <p class="text-sm PB-2">협찬 배너가 블로그에 등록되지 않았습니다.<br />리뷰 배너 등록방법을 참고해주세요</p>
        <button id="open-guideline" class="mt-4 px-4 py-2 rounded-full border border-[#46B6D0] text-[#46B6D0] text-sm hover:bg-[#46B6D0] hover:text-white transition">
          리뷰배너 등록방법
        </button>
      `,
      showConfirmButton: true,
      confirmButtonText: "확인",
      customClass: {
        confirmButton:
          "bg-red-500 hover:bg-[#0d1a5f] text-white py-3 px-10 rounded-full",
        popup: "rounded-lg shadow-lg text-sm",
      },
      didOpen: () => {
        const button = document.getElementById("open-guideline");
        if (button) {
          button.addEventListener("click", () => {
            Swal.close();
            setTimeout(() => {
              setShowGuideline(true);
            }, 300);
          });
        }
      },
    });
  };

  const handleGuidelineClose = () => setShowGuideline(false);

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    onNext();
  };

  return (
    <>
      <div className="">
        {/* Title */}
        <div className="border pb-2 flex flex-col gap-1 ps-3 bg-gray-100 rounded-lg pt-5">
          <h2 className="text-sm font-bold text-gray-800 mb-1">
            리뷰 작성하기
          </h2>
          <p className="text-xs text-red-500 mb-4">
            필수 키워드 등록 및 협찬배너 상단 등록 후 리뷰를 작성해주세요.
          </p>
        </div>

        {/* Keyword */}
        <div className="px-4 py-7 border-b">
          <div className="flex gap-2 items-center mb-3">
            <h3 className="flex item-center font-bold  text-sm">필수 키워드</h3>
            <button
              onClick={() => {
                const allKeywords = keywords.join(", ");
                navigator.clipboard.writeText(allKeywords);
                setCopiedKeyword("all");
                setTimeout(() => setCopiedKeyword(null), 2000);
              }}
              className={`flex items-center gap-2 text-xs border px-3 py-1 rounded-full transition-all duration-200 ${
                copiedKeyword === "all"
                  ? "border-green-300 text-green-600 bg-green-50"
                  : "border-blue-300 text-blue-600 hover:bg-blue-50"
              }`}
            >
              {copiedKeyword === "all" ? (
                <>
                  복사 완료!
                  <Check className="w-3 h-3" />
                </>
              ) : (
                <>
                  모든 키워드 복사
                  <Copy className="w-3 h-3" />
                </>
              )}
            </button>
          </div>

          <div className="flex gap-2 flex-wrap">
            {keywords.length > 0 ? (
              keywords.map((keyword: string, index: number) => (
                <div
                  key={index}
                  onClick={() => {
                    navigator.clipboard.writeText(keyword);
                    setCopiedKeyword(keyword);
                    setTimeout(() => setCopiedKeyword(null), 2000);
                  }}
                  className={`flex items-center justify-center gap-3 px-3 py-2 rounded-full border cursor-pointer transition-all duration-200 ${
                    copiedKeyword === keyword
                      ? "bg-green-100 border-green-300"
                      : "bg-gray-100 border-transparent hover:bg-gray-200"
                  }`}
                >
                  <span className="text-xs">{keyword}</span>
                  {copiedKeyword === keyword ? (
                    <Check className="w-3 h-3 text-green-600" />
                  ) : (
                    <Copy className="w-3 h-3 text-[#0F1E6C]" />
                  )}
                </div>
              ))
            ) : (
              <p className="text-xs text-gray-500">키워드가 없습니다.</p>
            )}
          </div>
        </div>

        {/* 블로그 작성 가이드 */}
        <div className="px-4 py-7 ">
          <h3 className="font-bold mb-3 text-sm">블로그 작성 가이드</h3>
          <ul className="space-y-4">
            {guidelines.length > 0 ? (
              guidelines.map(
                (item: GuidelineItem, i: number) =>
                  item.title && (
                    <li key={i} className="flex flex-col gap-1">
                      <div className="flex gap-2 items-start">
                        <Check className="w-3 h-3 text-white rounded-full bg-[#46B6D0] p-0.5 mt-1 flex-shrink-0" />
                        <span className="text-[#46B6D0] text-sm font-medium">
                          {item.title}
                        </span>
                      </div>
                      {item.description && (
                        <div className="pl-5 text-gray-500 text-xs mt-1 whitespace-pre-line">
                          {item.description}
                        </div>
                      )}
                    </li>
                  )
              )
            ) : (
              <li className="text-xs text-gray-500">데이터가 없습니다.</li>
            )}
          </ul>
        </div>

        {/* Input Review */}
        <div className="px-4 py-2 ">
          <h3 className="font-bold mb-2 text-sm">리뷰주소</h3>
          <input
            type="text"
            value={blogUrl}
            onChange={(e) => {
              setBlogUrl(e.target.value);
            }}
            className="w-full border border-gray-300 text-xs rounded-md py-3 "
            placeholder="작성한 블로그 포스팅 주소를 입력해 주세요"
          />
        </div>

        {/* Button Navigation */}
        <div className="max-w-xl mx-auto fixed z-20 bottom-0 left-0 right-0 bg-white border-t justify-between px-4 py-3 flex gap-2">
          <div
            onClick={onPrev}
            className="flex pr-2 text-sm rounded-full w-1/4 bg-[#F4F5FF] text-[#0F1E6C] h-11 justify-center items-center cursor-pointer"
          >
            <ChevronLeft />
            <div className="font-semibold">이전</div>
          </div>
          <Button
            onClick={submitReview}
            className="rounded-full w-3/4 bg-[#0F1E6C] text-white text-base h-11"
          >
            리뷰 등록
          </Button>
        </div>

        {/* Modal: Error Guide */}
        {showGuideline && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-lg w-full relative mx-4">
              <button
                onClick={handleGuidelineClose}
                className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 p-2"
              >
                <X size={20} />
              </button>
              <ModalTracking />
            </div>
          </div>
        )}

        {/* Modal: Success */}
        {showSuccessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full text-center">
              <Check className="text-green-500 w-10 h-10 mx-auto mb-4" />
              <h2 className="text-lg font-semibold mb-2">
                리뷰가 성공적으로 제출되었습니다!
              </h2>
              <Button
                className="mt-4 bg-[#0F1E6C] text-white w-full rounded-full"
                onClick={handleSuccessClose}
              >
                확인
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Review;
